import getButtons from './getButtons';
import setPasteOptionPosition from '../actions/setPasteOptionPosition';
import setPasteOptionSelectedIndex from '../actions/setPasteOptionSelectedIndex';
import { PASTE_TYPE } from '../store/schema/PasteOptionButton';
import PasteOptionViewState from '../store/schema/PasteOptionViewState';
import { logUsage } from 'owa-analytics';
import { EditorPluginEx, PluginUtils, PluginViewStateAdapter, PluginWithUI } from 'owa-editor';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { Editor } from 'roosterjs-editor-core';
import { ClipboardData, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import PasteOption from '../components/PasteOption';

const CTRL_KEY = 17;
const ESC_KEY = 27;
const LEFT_KEY = 37;
const RIGHT_KEY = 39;
const ENTER_KEY = 13;
const PASTE_OPTION_DELAY_TIME = 200;

export default class PasteOptionPlugin
    implements EditorPluginEx, PluginWithUI<PasteOptionViewState> {
    private clipboardData: ClipboardData;
    private pluginUtils: PluginUtils;
    private editor: Editor;
    private timer: number;

    constructor(
        readonly adapter: PluginViewStateAdapter<PasteOptionViewState>,
        private isRtl: boolean
    ) {}

    getName() {
        return 'PasteOption';
    }

    initialize(editor: Editor) {
        this.editor = editor;
    }

    dispose() {
        this.dismiss();
        this.editor = null;
    }

    setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
    }

    getUIComponentClass() {
        return PasteOption;
    }

    onPluginEvent(event: PluginEvent) {
        if (event.eventType == PluginEventType.Scroll) {
            this.onScroll();
        }

        const viewState = this.adapter();

        if (!viewState.position) {
            this.handleEventForEditor(event);
        } else if (viewState.subMenuSelectionIndex < 0) {
            this.handleEventForCollapsedPastePane(event);
        } else if (event.eventType === PluginEventType.ContentChanged) {
            this.dismiss();
        } else {
            this.handleEventForExpandedPastePane(event);
        }
    }

    dismiss() {
        this.cancelShowPasteOptionPane();
        setPasteOptionPosition(this.adapter(), null);
        this.clipboardData = null;
    }

    paste(key: PASTE_TYPE) {
        if (this.clipboardData && this.pluginUtils) {
            switch (key) {
                case 'PASTE_ORIGINAL':
                    this.pluginUtils.pasteOriginal(this.clipboardData);
                    break;

                case 'PASTE_TEXT':
                    this.pluginUtils.pasteText(this.clipboardData);
                    break;
            }

            logUsage('PasteOptionUsage', [key]);

            let keys = getButtons().map(button => button.key);
            let newIndex = keys.indexOf(key);
            if (newIndex >= 0 && newIndex < keys.length) {
                setPasteOptionSelectedIndex(this.adapter(), newIndex);
            }
        }
    }

    private handleEventForEditor(event: PluginEvent) {
        if (event.eventType == PluginEventType.ContentChanged && event.source == 'Paste') {
            let clipboardData = event.data as ClipboardData;

            // Only show paste option when we pasted HTML with some format
            if (
                clipboardData &&
                clipboardData.text &&
                ((clipboardData.types && clipboardData.types.indexOf('text/html') >= 0) ||
                    (isBrowserIE() && !clipboardData.image))
            ) {
                this.clipboardData = clipboardData;
                this.showPasteOptionPane();
            }
        }
    }

    private handleEventForCollapsedPastePane(event: PluginEvent) {
        if (event.eventType == PluginEventType.KeyDown) {
            let keyboardEvent = event.rawEvent;
            if (keyboardEvent.which == CTRL_KEY) {
                setPasteOptionSelectedIndex(this.adapter(), 0);
                this.cancelEvent(keyboardEvent);
            } else if (keyboardEvent.which == ESC_KEY) {
                this.dismiss();
                this.cancelEvent(keyboardEvent);
            } else {
                this.dismiss();
            }
        } else if (event.eventType == PluginEventType.MouseDown) {
            this.dismiss();
        }
    }

    private handleEventForExpandedPastePane(event: PluginEvent) {
        const viewState = this.adapter();
        const subMenuSelectionIndex = viewState.subMenuSelectionIndex;

        if (event.eventType == PluginEventType.KeyDown) {
            const keyboardEvent = event.rawEvent;
            if (keyboardEvent.which != CTRL_KEY && keyboardEvent.ctrlKey) {
                // Dismiss the paste option when pressing hotkey CTRL+<any key>
                this.dismiss();
                return;
            }

            let buttons = getButtons();

            for (let button of buttons) {
                if (button.shortcut == String.fromCharCode(keyboardEvent.which)) {
                    this.paste(button.key);
                    this.cancelEvent(keyboardEvent);
                    return;
                }
            }

            switch (keyboardEvent.which) {
                case ESC_KEY:
                    this.dismiss();
                    break;

                case LEFT_KEY:
                case RIGHT_KEY:
                    let buttonCount = buttons.length;
                    let diff = (keyboardEvent.which == RIGHT_KEY) == this.isRtl ? -1 : 1;
                    setPasteOptionSelectedIndex(
                        viewState,
                        (subMenuSelectionIndex + diff + buttonCount) % buttonCount
                    );
                    break;

                case ENTER_KEY:
                    this.paste(buttons[subMenuSelectionIndex].key);
                    break;

                case CTRL_KEY:
                    // Noop
                    break;

                default:
                    this.dismiss();
                    return;
            }

            this.cancelEvent(keyboardEvent);
        } else if (event.eventType == PluginEventType.MouseDown) {
            this.dismiss();
        }
    }

    private cancelShowPasteOptionPane() {
        if (this.timer) {
            window.clearTimeout(this.timer);
        }
    }

    private showPasteOptionPane() {
        this.timer = window.setTimeout(() => {
            this.timer = 0;
            if (this.editor) {
                const viewState = this.adapter();
                const isAlreadyShowing = !!viewState.position;
                const rect = this.editor.getCursorRect();
                if (rect) {
                    setPasteOptionPosition(viewState, {
                        x: this.isRtl ? rect.left : rect.right,
                        y: rect.bottom,
                    });
                    if (!isAlreadyShowing) {
                        setPasteOptionSelectedIndex(viewState, -1);
                    }
                }
            }
        }, PASTE_OPTION_DELAY_TIME);
    }

    private cancelEvent(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    private onScroll = () => {
        if (this.adapter().position) {
            this.showPasteOptionPane();
        }
    };
}
